/**
 * TenantTreeView — the ONE tenant tree.
 *
 * The same object renders in the ⌘K switcher and in the console's Tenants
 * tab. They used to be two different trees (the switcher's guide-line rows vs
 * the Admin page's connector-card rows), which meant the operator learned one
 * shape and then met another a click later.
 *
 * Rows are a flattened tree with the classic guide-line algorithm: `guides[i]`
 * says whether the ancestor line at depth i continues past this row. Search
 * keeps each match's ancestors so a result is never shown out of context —
 * they render dimmed, because they are context, not matches.
 *
 * Collapsing is optional: the switcher wants every tenant visible at once,
 * while the Tenants tab is a working surface where branches should fold away.
 * Pass `collapsed`/`onToggleCollapse` to turn it on.
 */

import { useMemo } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { getLevelIcon, levelStyles } from '@/components/admin/HierarchyTreeNode';
import { TenantChip, type SwitcherTenant } from './TenantSwitcher';

export interface TenantTreeRow {
  tenant: SwitcherTenant;
  depth: number;
  guides: boolean[];
  isLast: boolean;
  /** Matched the query itself, vs. kept only as an ancestor of a match. */
  match: boolean;
  hasChildren: boolean;
}

interface TreeNode {
  tenant: SwitcherTenant;
  children: TreeNode[];
}

function buildTree(tenants: SwitcherTenant[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  tenants.forEach(t => byId.set(t.tenant_id, { tenant: t, children: [] }));
  const roots: TreeNode[] = [];
  byId.forEach(node => {
    const pid = node.tenant.parent_tenant_id;
    const parent = pid ? byId.get(pid) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  });
  const byName = (a: TreeNode, b: TreeNode) => a.tenant.tenant_name.localeCompare(b.tenant.tenant_name);
  const sortRec = (nodes: TreeNode[]) => { nodes.sort(byName); nodes.forEach(n => sortRec(n.children)); };
  sortRec(roots);
  return roots;
}

/** Keep a node if it matches or any descendant does; flag the real matches. */
function filterTree(nodes: TreeNode[], q: string): Array<TreeNode & { match: boolean }> {
  const out: Array<TreeNode & { match: boolean }> = [];
  for (const n of nodes) {
    const self =
      n.tenant.tenant_name.toLowerCase().includes(q) ||
      n.tenant.tenant_code.toLowerCase().includes(q);
    const kids = filterTree(n.children, q);
    if (self || kids.length) out.push({ tenant: n.tenant, children: kids, match: self });
  }
  return out;
}

function flatten(
  nodes: Array<TreeNode & { match?: boolean }>,
  depth: number,
  trail: boolean[],
  out: TenantTreeRow[],
  allMatch: boolean,
  collapsed?: Set<string>,
) {
  nodes.forEach((n, i) => {
    const isLast = i === nodes.length - 1;
    const hasChildren = n.children.length > 0;
    out.push({
      tenant: n.tenant,
      depth,
      guides: trail,
      isLast,
      match: allMatch || !!n.match,
      hasChildren,
    });
    // A collapsed branch keeps its row but hides its subtree. While a query is
    // active we ignore collapse entirely — a match must never hide behind a
    // closed parent.
    if (hasChildren && !(collapsed && collapsed.has(n.tenant.tenant_id))) {
      flatten(
        n.children as Array<TreeNode & { match?: boolean }>,
        depth + 1,
        [...trail, !isLast],
        out,
        allMatch,
        collapsed,
      );
    }
  });
}

/** Build the visible rows for a tenant list. Exported so callers can count
 *  matches without rendering (the switcher shows "N matches"). */
export function useTenantTreeRows(
  tenants: SwitcherTenant[],
  query: string,
  collapsed?: Set<string>,
): TenantTreeRow[] {
  const roots = useMemo(() => buildTree(tenants), [tenants]);
  const q = query.trim().toLowerCase();
  return useMemo(() => {
    const out: TenantTreeRow[] = [];
    if (q) flatten(filterTree(roots, q), 0, [], out, false, undefined);
    else flatten(roots, 0, [], out, true, collapsed);
    return out;
  }, [roots, q, collapsed]);
}

/** Wrap the matched substring so the eye lands on why a row is here. */
export function highlight(text: string, q: string): React.ReactNode {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q);
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark>{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}

interface Props {
  rows: TenantTreeRow[];
  query: string;
  /** Row rendered as "you are here" — not selectable in the switcher. */
  currentTenantId?: string | null;
  /** Row the keyboard is on (switcher) or the last-opened tenant (directory). */
  activeTenantId?: string | null;
  onSelect: (tenantId: string) => void;
  onHover?: (tenantId: string) => void;
  /** Turns on the expand/collapse chevrons. */
  collapsed?: Set<string>;
  onToggleCollapse?: (tenantId: string) => void;
  /** Extra content after the code (cascade badges in the directory). */
  renderBadges?: (tenantId: string) => React.ReactNode;
  /** Trailing content (the directory's "Open workspace →" hint). */
  renderTrailing?: (tenantId: string) => React.ReactNode;
  /** Ref for the active row so callers can scroll it into view. */
  activeRef?: React.Ref<HTMLButtonElement>;
  /** data-testid prefix — the switcher and directory are asserted separately. */
  testIdPrefix?: string;
}

export default function TenantTreeView({
  rows,
  query,
  currentTenantId,
  activeTenantId,
  onSelect,
  onHover,
  collapsed,
  onToggleCollapse,
  renderBadges,
  renderTrailing,
  activeRef,
  testIdPrefix = 'switcher-tenant',
}: Props) {
  const { t } = useLanguage();
  const q = query.trim().toLowerCase();

  return (
    <>
      {rows.map(row => {
        const id = row.tenant.tenant_id;
        const isCurrent = id === currentTenantId;
        const isActive = id === activeTenantId;
        const style = levelStyles[Math.min(row.depth, levelStyles.length - 1)];
        const isCollapsed = !!collapsed?.has(id);
        return (
          <button
            key={id}
            ref={isActive ? activeRef : undefined}
            type="button"
            role="treeitem"
            aria-level={row.depth + 1}
            aria-selected={isActive}
            aria-expanded={row.hasChildren && onToggleCollapse ? !isCollapsed : undefined}
            aria-disabled={isCurrent || undefined}
            className={[
              'sa-switcher-node',
              isActive ? 'sa-switcher-node--active' : '',
              q && !row.match ? 'sa-switcher-node--dim' : '',
              isCurrent ? 'sa-switcher-node--current' : '',
            ].join(' ')}
            onMouseEnter={onHover ? () => onHover(id) : undefined}
            onClick={() => onSelect(id)}
            data-testid={`${testIdPrefix}-${row.tenant.tenant_code}`}
            data-tenant-id={id}
            data-tenant-code={row.tenant.tenant_code}
          >
            {Array.from({ length: row.depth }, (_, d) => {
              const continues = d < row.depth - 1 ? row.guides[d + 1] : !row.isLast;
              return (
                <span
                  key={d}
                  className={[
                    'sa-switcher-guide',
                    continues ? '' : 'sa-switcher-guide--last',
                    d === row.depth - 1 ? 'sa-switcher-guide--tick' : '',
                  ].join(' ')}
                  aria-hidden
                />
              );
            })}

            {/* Chevron only where collapsing is on AND there is something to
                fold. A <span> rather than a nested <button>: this row is
                already a button, and buttons cannot nest. */}
            {onToggleCollapse && (
              <span
                className={`sa-tree-chevron ${row.hasChildren ? '' : 'sa-tree-chevron--empty'}`}
                onClick={row.hasChildren ? (e => { e.stopPropagation(); onToggleCollapse(id); }) : undefined}
                role={row.hasChildren ? 'button' : undefined}
                aria-label={row.hasChildren
                  ? t(isCollapsed ? 'superAdmin.console.treeExpand' : 'superAdmin.console.treeCollapse')
                  : undefined}
                aria-hidden={row.hasChildren ? undefined : true}
              >
                {row.hasChildren ? (isCollapsed ? '▶' : '▼') : ''}
              </span>
            )}

            {getLevelIcon(row.depth, style.color)}
            <TenantChip tenant={row.tenant} />
            <span className="sa-switcher-node-name">{highlight(row.tenant.tenant_name, q)}</span>
            <span className="sa-switcher-node-code">{highlight(row.tenant.tenant_code, q)}</span>
            {renderBadges?.(id)}
            <span className="sa-switcher-node-meta">
              {renderTrailing?.(id)}
              {row.tenant.user_count !== undefined && (
                <span
                  className="sa-switcher-node-count"
                  title={t('superAdmin.workspace.usersCount', { count: row.tenant.user_count })}
                >
                  {row.tenant.user_count}
                </span>
              )}
              {isCurrent && (
                <span className="sa-switcher-current-tag">
                  {t('superAdmin.console.switcherCurrent')}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </>
  );
}
